/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { connect } from 'react-redux'
import loadable from '@loadable/component'

import { EuiSkeletonText } from '@elastic/eui'

import type { StackDeployment } from '@modules/ui-types'
import Feature from '@modules/utils/feature'
import withRouter from '@modules/routing/withRouter'

import {
  getFirstEsClusterFromGet,
  getVersion,
  getRegionId,
} from '@/lib/stackDeployments/selectors/fundamentals'
import { getDeploymentTemplateId } from '@/lib/stackDeployments/selectors/deploymentTemplates'
import { getStackDeployment, getDeletedStackDeploymentIds } from '@/reducers/stackDeployments'
import { getDeploymentTemplate } from '@/reducers/deploymentTemplates'
import { getCluster } from '@/reducers/clusters'
import { getApm } from '@/reducers/apms'
import { fetchStackDeploymentRequest } from '@/reducers/asyncRequests/registry'

import { clearApmToken } from '../../../actions/apm'
import { clearClusterCredentials } from '../../../actions/clusters'
import { clearCreateDeploymentResponse } from '../../../actions/stackDeployments'
import { isFeatureActivated } from '../../../store'

import withStackDeploymentPolling from './withStackDeploymentPolling'

import type { ReduxState } from '@/types/redux'
import type { StateProps, DispatchProps, ConsumerProps } from './types'

const DeploymentDependencyLoader = loadable(() => import(`./DeploymentDependencyLoader`), {
  fallback: <EuiSkeletonText />,
})

const mapStateToProps = (state: ReduxState, { stackDeploymentId }: ConsumerProps): StateProps => {
  const deployment = getStackDeployment(state, stackDeploymentId)
  const esClusterId = deployment && getEsClusterId({ deployment })
  const regionId = deployment && getRegionId({ deployment })
  const esCluster = getCluster(state, regionId!, esClusterId!)
  const version = deployment ? getVersion({ deployment }) : null

  const deploymentTemplate: any = deployment
    ? getDeploymentTemplate(
        state,
        regionId!,
        getDeploymentTemplateId({ deployment })!,
        getVersion({ deployment }),
      )
    : null

  const deletedDeploymentIds = getDeletedStackDeploymentIds(state)
  const wasDeleted = deletedDeploymentIds.includes(stackDeploymentId)

  return {
    apm: esCluster ? getApm(state, regionId!, esCluster.apm.id!) : undefined,
    deployment: esCluster,
    deploymentId: esClusterId!,
    deploymentRequest: fetchStackDeploymentRequest(state, stackDeploymentId),
    deploymentTemplate,
    regionId: regionId!,
    shouldShowRegion: isFeatureActivated(Feature.regionNames),
    stackDeployment: deployment,
    titleId: stackDeploymentId,
    wasDeleted,
    isFullStoryActivated: isFeatureActivated(Feature.userconsoleRunFullStory),
    version,
  }
}

const mapDispatchToProps: DispatchProps = {
  clearCreateDeploymentResponse,
  clearClusterCredentials,
  clearApmToken,
}

export default withRouter(
  connect<StateProps, DispatchProps, ConsumerProps>(
    mapStateToProps,
    mapDispatchToProps,
  )(withStackDeploymentPolling(DeploymentDependencyLoader)),
)

function getEsClusterId({ deployment }: { deployment: StackDeployment | null }): string | null {
  if (!deployment) {
    return null
  }

  const esCluster = getFirstEsClusterFromGet({ deployment })

  if (!esCluster) {
    return null
  }

  return esCluster.id
}
